import { palette } from '@kimuno/theme';
import { ArrowDropDown } from '@mui/icons-material';
import {
	Button,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Tooltip,
	Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type React from 'react';
import { useState } from 'react';

export interface ToolbarMenuItem {
	label: string;
	icon?: React.ReactNode;
	selected?: boolean;
	onClick: () => void;
}

interface ToolbarMenuButtonProps {
	title: string;
	dataTestId?: string;
	label?: string;
	startIcon?: React.ReactNode;
	isActive?: boolean;
	menuItems: ToolbarMenuItem[];
}

const ToolbarMenuButton: React.FC<ToolbarMenuButtonProps> = ({
	title,
	dataTestId,
	label,
	startIcon,
	isActive = false,
	menuItems,
}) => {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	// Generate a unique ID for the button to use with aria-labelledby
	const buttonId = dataTestId || `toolbar-menu-button-${title.replace(/\s+/g, '-').toLowerCase()}`;

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleMenuItemClick = (itemOnClick: () => void) => {
		itemOnClick();
		handleClose();
	};

	return (
		<>
			<Tooltip title={title} leaveDelay={0} leaveTouchDelay={0}>
				<Button
					id={buttonId}
					data-testid={dataTestId}
					size="small"
					variant="text"
					color="secondary"
					startIcon={startIcon}
					endIcon={<ArrowDropDown aria-hidden="true" />}
					onClick={handleClick}
					type="button"
					aria-label={title}
					aria-haspopup="true"
					aria-expanded={Boolean(anchorEl)}
					sx={{
						minWidth: '48px',
						backgroundColor: isActive ? alpha(palette.primary.main, 0.08) : 'transparent',
						'&:hover': {
							backgroundColor: alpha(palette.primary.main, 0.06),
						},
					}}
				>
					{label && <Typography fontWeight={600}>{label}</Typography>}
				</Button>
			</Tooltip>
			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleClose}
				sx={{
					zIndex: 1500,
				}}
				MenuListProps={{
					'aria-labelledby': buttonId,
				}}
			>
				{menuItems.map((item, index) => (
					<MenuItem
						// biome-ignore lint/suspicious/noArrayIndexKey: index keys are pre-existing; switching the key source changes reconciliation behavior
						key={index}
						selected={item.selected}
						onClick={() => handleMenuItemClick(item.onClick)}
					>
						{item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
						<ListItemText>{item.label}</ListItemText>
					</MenuItem>
				))}
			</Menu>
		</>
	);
};

export default ToolbarMenuButton;
